import { inject, Injectable, signal } from '@angular/core';
import { COMMONS_ENV } from '@damofire/data-angular-commons';
import { getRaw, HttpUtils, logger } from '@damofire/data-commons';
import { isConnectionError, notifyPersistenceFailure, withConnectionRetry } from '../db/dexie-hardening';
import { HubspotCompanyDatabase } from '../db/hubspot-company.db';
import { HubspotCompany } from '../models/hubspot-company.model';

const BUNDLE_ENDPOINT = 'nexus-surveys/companies/bundle';
const META_VERSION_KEY = 'bundleVersion';
const DEXIE_WRITE_CHUNK_SIZE = 5000;

interface BundleManifest {
  url: string;
  version: string;
}

interface BundlePayload {
  version: string;
  companies: HubspotCompany[];
}

@Injectable({ providedIn: 'root' })
export class HubspotCompanyService {
  private readonly db = inject(HubspotCompanyDatabase);
  private readonly environment = inject(COMMONS_ENV);

  readonly localCount = signal(0);
  readonly refreshing = signal(false);

  private localDataReady?: Promise<void>;

  ensureLocalData(): Promise<void> {
    this.localDataReady ??= this.loadLocalData();
    return this.localDataReady;
  }

  async searchCompanies(term: string, resultLimit = 20): Promise<HubspotCompany[]> {
    const normalizedTerm = term.trim().toLocaleLowerCase();
    if (!normalizedTerm) {
      return [];
    }
    await this.ensureLocalData();
    return this.db.companies
      .filter((company) => company.name.toLocaleLowerCase().includes(normalizedTerm) || company.hubspotId.includes(term.trim()))
      .limit(resultLimit)
      .toArray();
  }

  async refreshLocalCompanies(): Promise<{ count: number; version: string; alreadyUpToDate: boolean }> {
    if (this.refreshing()) {
      return { count: this.localCount(), version: '', alreadyUpToDate: true };
    }
    this.refreshing.set(true);
    try {
      const manifest = await HttpUtils.get<BundleManifest>(this.environment.appConfig.fns, BUNDLE_ENDPOINT);
      const [localVersion, localCount] = await Promise.all([this.readLocalVersion(), this.db.companies.count()]);
      if (manifest.version === localVersion && localCount > 0) {
        this.localCount.set(localCount);
        return { count: localCount, version: manifest.version, alreadyUpToDate: true };
      }

      const payload = await getRaw<BundlePayload>(manifest.url, 'json');
      await this.applyBundlePayload(payload);
      this.localCount.set(payload.companies.length);
      return { count: payload.companies.length, version: payload.version, alreadyUpToDate: false };
    } finally {
      this.refreshing.set(false);
    }
  }

  private async loadLocalData(): Promise<void> {
    try {
      const count = await this.db.companies.count();
      this.localCount.set(count);
      if (count === 0) {
        await this.refreshLocalCompanies();
      }
    } catch (error) {
      logger.error('Failed to load local HubSpot companies', { category: 'sync', error });
      this.localDataReady = undefined;
    }
  }

  private async applyBundlePayload(payload: BundlePayload): Promise<void> {
    try {
      await withConnectionRetry('companies.applyBundlePayload', () =>
        this.db.transaction('rw', this.db.companies, this.db.meta, async () => {
          await this.db.companies.clear();
          for (let offset = 0; offset < payload.companies.length; offset += DEXIE_WRITE_CHUNK_SIZE) {
            await this.db.companies.bulkPut(payload.companies.slice(offset, offset + DEXIE_WRITE_CHUNK_SIZE));
          }
          await this.db.meta.put({ key: META_VERSION_KEY, value: payload.version });
        }),
      );
    } catch (error) {
      const attempts = isConnectionError(error) ? 2 : 1;
      notifyPersistenceFailure(this.db.persistenceFailureHandler, {
        storeName: 'companies_meta',
        operation: 'applyBundlePayload',
        error,
        attempts,
        happenedAt: new Date().toISOString(),
      });
      throw error;
    }
  }

  private async readLocalVersion(): Promise<string | null> {
    const entry = await this.db.meta.get(META_VERSION_KEY);
    return entry?.value ?? null;
  }
}
